import React from 'react';
import { useTranslation } from 'react-i18next';
import compose from 'lodash/fp/compose';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { withTranslation, withAccount } from 'components/hoc';
import DashboardTemplate from '../common/DashboardTemplate';
import routes, { redirects } from './routes';
import Logo from './Admincatlogo.png';
import LogoInverted from './AdminCatinverted.png';
import { useEffectiveDark } from './common/AuthGatedTheme';

const FooterWrapper = styled.footer`
  width: 100%;
  margin-top: 0;
  padding: ${({ $public }) =>
    $public ? '76px clamp(40px, 5.25vw, 104px) 38px' : '60px 5% 30px'};
  background: ${({ $dark }) => ($dark ? '#0e1526' : '#ffffff')};
  color: ${({ $dark }) => ($dark ? '#dce4ff' : '#4d6286')};
  border-top: 1px solid ${({ $dark }) => ($dark ? 'rgba(133, 153, 195, 0.28)' : '#e2e8f0')};
  box-sizing: border-box;
  font-family: ${({ $public }) =>
    $public ? "'DM Sans', Helvetica, Arial, sans-serif" : 'inherit'};
  font-size: ${({ $public }) => ($public ? '16px' : 'inherit')};
  transition: background 0.3s ease, color 0.3s ease;

  @media (max-width: 900px) {
    padding: ${({ $public }) =>
      $public ? '60px clamp(28px, 5vw, 44px) 30px' : '60px 5% 30px'};
  }

  @media (max-width: 640px) {
    padding: ${({ $public }) => ($public ? '48px 20px 24px' : '44px 20px 22px')};
  }
`;

const FooterContainer = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 2fr) repeat(3, minmax(150px, 1fr));
  gap: 48px;
  align-items: flex-start;
  margin-bottom: ${({ $public }) => ($public ? '66px' : '48px')};

  @media (max-width: 980px) {
    grid-template-columns: minmax(220px, 1.3fr) repeat(3, minmax(140px, 1fr));
    gap: 42px 34px;
  }

  @media (max-width: 820px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 34px;
    margin-bottom: 44px;
  }
`;

const BrandColumn = styled.div`
  max-width: 100%;
`;

const LogoImage = styled.img`
  width: ${({ $public }) => ($public ? '174px' : '150px')};
  height: auto;
  display: block;
  margin-bottom: ${({ $public }) => ($public ? '12px' : '10px')};

  @media (max-width: 560px) {
    width: 142px;
  }
`;

const BrandTagline = styled.p`
  margin: 0;
  max-width: 260px;
  font-size: ${({ $public }) => ($public ? '1rem' : '0.88rem')};
  line-height: ${({ $public }) => ($public ? '1.64' : '1.65')};
  font-weight: 400;
  color: ${({ $dark }) => ($dark ? '#b7c5eb' : '#52678b')};
`;

const SocialList = styled.ul`
  display: flex;
  align-items: center;
  gap: 12px;
  list-style: none;
  margin: ${({ $public }) => ($public ? '22px 0 0' : '20px 0 0')};
  padding: 0;

  li {
    margin: 0;
    padding: 0;
  }
`;

const SocialButton = styled(Link)`
  width: ${({ $public }) => ($public ? '42px' : '36px')};
  height: ${({ $public }) => ($public ? '42px' : '36px')};
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: ${({ $dark }) => ($dark ? '#cde0ff' : '#2f68b8')};
  background: ${({ $dark }) => ($dark ? 'rgba(98, 139, 202, 0.16)' : '#f0f6ff')};
  font-size: ${({ $public }) => ($public ? '0.96rem' : '0.85rem')};
  font-weight: 500;
  line-height: 1;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;

  &:hover {
    color: ${({ $dark }) => ($dark ? '#ffffff' : '#073b78')};
    background: ${({ $dark }) => ($dark ? 'rgba(98, 139, 202, 0.28)' : '#e5f0ff')};
    transform: translateY(-1px);
  }
`;

const FooterColumn = styled.nav`
  margin: 0;
  padding: 0;
`;

const ColumnTitle = styled.h2`
  margin: 0 0 16px;
  color: ${({ $dark }) => ($dark ? '#f0f5ff' : '#073b78')};
  font-family: 'Sora', 'DM Sans', Helvetica, Arial, sans-serif;
  font-size: ${({ $public }) => ($public ? '0.92rem' : '0.85rem')};
  line-height: 1.2;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const LinkList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 10px;

  li {
    margin: 0;
    padding: 0;
  }
`;

const FooterLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: 18px;
  margin: 0;
  padding: 0;
  color: ${({ $dark }) => ($dark ? '#c2d0ec' : '#52678b')};
  font-size: ${({ $public }) => ($public ? '0.96rem' : '0.88rem')};
  line-height: 1.35;
  text-decoration: none;
  transition: color 0.2s ease, transform 0.2s ease;

  &:hover {
    color: ${({ $dark }) => ($dark ? '#ffffff' : '#073b78')};
    transform: translateX(2px);
  }
`;

const FooterBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding-top: ${({ $public }) => ($public ? '28px' : '24px')};
  border-top: 1px solid ${({ $dark }) => ($dark ? 'rgba(133, 153, 195, 0.28)' : '#d8e2f1')};
  color: ${({ $dark }) => ($dark ? '#c5d3f2' : '#52678b')};
  font-size: ${({ $public }) => ($public ? '0.9rem' : '0.82rem')};
  line-height: 1.5;

  @media (max-width: 720px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const CopyrightText = styled.p`
  margin: 0;
`;

const Heart = styled.span`
  color: ${({ $dark }) => ($dark ? '#f38ba8' : '#52678b')};
`;

const StatusPill = styled(Link)`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border-radius: 999px;
  color: ${({ $dark }) => ($dark ? '#cae0ff' : '#2f68b8')};
  background: ${({ $dark }) => ($dark ? 'rgba(98, 139, 202, 0.16)' : '#f0f6ff')};
  text-decoration: none;
  font-size: ${({ $public }) => ($public ? '0.84rem' : '0.78rem')};
  line-height: 1;
  white-space: nowrap;
`;

const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #18d37b;
`;

const footerGroups = [
  {
    title: 'Product',
    links: [
      { label: 'Features', path: '/features' },
      { label: 'Security Hub', path: '/security-hub' },
      { label: 'Modules', path: '/modules' },
      { label: 'Roadmap', path: '/roadmap' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About Us', path: '/about-us' },
      { label: 'Blog', path: '/#blog' },
      { label: 'FAQ', path: '/faq' },
      { label: 'Disclaimer', path: '/disclaimer' },
      { label: 'Privacy Policy', path: '/privacy-statement' },
      { label: 'Terms of Service', path: '/user-terms' },
      { label: 'Contact', path: '/contact' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Documentation', path: '/documentation' },
      { label: 'Community Forum', path: '/community-forum' },
      { label: 'API Status', path: '/api-status' },
      { label: 'Help Center', path: '/help-center' },
    ],
  },
];

const socialLinks = [
  { label: 'X', path: '/social/x', icon: '𝕏' },
  { label: 'LinkedIn', path: '/social/linkedin', icon: 'in' },
  { label: 'Facebook', path: '/social/facebook', icon: 'f' },
];

const Footer = (props) => {
  const { tr } = props;
  const location = useLocation();
  const { t } = useTranslation();
  const isAuth = Boolean(props.account && props.account.user && props.account.user.isAuth);
  const effectiveDark = useEffectiveDark(isAuth);
  const logoSrc = effectiveDark ? LogoInverted : Logo;
  const currentYear = new Date().getFullYear();
  const translate = (key) => (isAuth ? tr(key) : t(key));

  if (location.pathname === '/admin') {
    return null;
  }

  const footerContent = (
    <FooterWrapper $dark={effectiveDark} $public={!isAuth}>
      <FooterContainer>
        <FooterTop $public={!isAuth}>
          <BrandColumn>
            <LogoImage $public={!isAuth} src={logoSrc} alt="AdminCat" />
            <BrandTagline $dark={effectiveDark} $public={!isAuth}>
              {translate(
                'The ultimate dashboard for the modern digital sovereign. Managed in Germany, deployed globally.',
              )}
            </BrandTagline>
            <SocialList $public={!isAuth} aria-label={translate('Social links')}>
              {socialLinks.map(({ label, path, icon }) => (
                <li key={path}>
                  <SocialButton
                    $dark={effectiveDark}
                    $public={!isAuth}
                    to={path}
                    aria-label={label}
                    className="social-btn"
                  >
                    {icon}
                  </SocialButton>
                </li>
              ))}
            </SocialList>
          </BrandColumn>

          {footerGroups.map(({ title, links }) => (
            <FooterColumn key={title} aria-label={translate(title)}>
              <ColumnTitle $dark={effectiveDark} $public={!isAuth}>
                {translate(title)}
              </ColumnTitle>
              <LinkList>
                {links.map(({ label, path }) => (
                  <li key={path}>
                    <FooterLink $dark={effectiveDark} $public={!isAuth} to={path}>
                      {translate(label)}
                    </FooterLink>
                  </li>
                ))}
              </LinkList>
            </FooterColumn>
          ))}
        </FooterTop>

        <FooterBottom $dark={effectiveDark} $public={!isAuth}>
          <CopyrightText>
            &copy; {currentYear} AdminCat Infrastructure GmbH.{' '}
            {translate('All rights reserved')}. {translate('Made with')}{' '}
            <Heart $dark={effectiveDark}>{'\u2764'}</Heart>{' '}
            {translate('from Germany & Nigeria')}
          </CopyrightText>
          <StatusPill $dark={effectiveDark} $public={!isAuth} to="/api-status">
            <StatusDot aria-hidden="true" />
            {translate('All systems operational')}
          </StatusPill>
        </FooterBottom>
      </FooterContainer>
    </FooterWrapper>
  );

  if (isAuth) {
    return (
      <DashboardTemplate routes={routes} redirects={redirects}>
        {footerContent}
      </DashboardTemplate>
    );
  }

  return footerContent;
};

export default compose(withTranslation, withAccount)(Footer);
